import { useAuthenticatedApi } from "../hooks/useAuthenticatedApi";

// Response interfaces matching backend structure
export interface GuestDirectoryItem {
  guestId: string;
  name: {
    title: string | null;
    firstName: string;
    lastName: string;
    fullName: string;
  };
  dateOfBirth: string | null;
  gender: string | null;
  contact: {
    email: string | null;
    phone: string | null;
  };
  currentStay: {
    reservationId: string;
    room: {
      roomNumber: string;
      building: string | null;
    } | null;
    checkIn: string;
    checkOut: string;
    status: string;
    accompanyingGuests: Array<{ guestId: string; name: string }>;
  } | null;
  dietaryRestrictions: Array<{ code: string; name: string }>;
  profileCreated: string;
  isActive: boolean;
}

export interface GuestPreference {
  preferenceId: string;
  type: string;
  value: string;
  lastUpdated: string;
}

export interface GuestDetailedProfile {
  guestId: string;
  identity: {
    name: {
      title: string | null;
      firstName: string;
      middleName: string | null;
      lastName: string;
      suffix: string | null;
      fullName: string;
    };
    dateOfBirth: string | null;
    gender: string | null;
    contact: {
      primaryEmail: string | null;
      primaryPhone: string | null;
      additionalEmails: Array<{
        address: string;
        type: string;
        isPrimary: boolean;
        isVerified: boolean;
      }>;
      additionalPhones: Array<{
        number: string;
        type: string;
        isPrimary: boolean;
        isVerified: boolean;
      }>;
    };
  };
  stayInformation: {
    currentStay: {
      reservationId: string;
      masterReservationId: string;
      room: {
        roomNumber: string;
        building: string | null;
        floor: number | null;
      } | null;
      checkIn: {
        estimated: string;
        actual: string | null;
      };
      checkOut: {
        estimated: string;
        actual: string | null;
      };
      status: string;
      otherGuestsInBooking: Array<{
        guestId: string;
        name: string;
      }>;
    } | null;
    recentStays: Array<{
      reservationId: string;
      room: {
        roomNumber: string;
        building: string | null;
      } | null;
      checkIn: string;
      checkOut: string;
      status: string;
    }>;
  };
  billing: {
    totalBill: number;
    unpaidAmount: number;
    recentCharges: Array<{
      chargeId: string;
      description: string;
      amount: number;
      currency: string;
      isPaid: boolean;
      chargeDate: string;
      serviceDate: string | null;
    }>;
  };
  preferences: GuestPreference[];
  requestHistory: Array<{
    requestId: string;
    type: string;
    name: string;
    status: string;
    createdAt: string;
    completedAt: string | null;
  }>;
  profileInfo: {
    profileCreated: string;
    profileLastModified: string;
    isActive: boolean;
    rewardsInfo: Record<string, unknown> | null; // JSON field
    hotelSpecificPreferences: Record<string, unknown> | null; // JSON field
  };
}

export interface GuestDirectoryResponse {
  data: GuestDirectoryItem[];
  meta: {
    page: number;
    limit: number;
    totalCount: number;
    totalPages: number;
    status: string;
    search: string | null;
  };
}

export interface PreferenceUpdateRequest {
  preferences: Array<{
    type: string;
    value: string;
  }>;
}

export interface PreferenceUpdateResponse {
  message: string;
  preferences: GuestPreference[];
}

// Custom error class for better error handling
class GuestProfileError extends Error {
  constructor(message: string, public code?: string) {
    super(message);
    this.name = "GuestProfileError";
  }
}

// API class
export class GuestProfilesApi {
  constructor(
    private callApi: ReturnType<typeof useAuthenticatedApi>["callApi"]
  ) {}

  /**
   * Get guest directory with filtering and search
   */
  async getGuestDirectory(
    params: {
      status?: "Current" | "Historic" | "Future" | "All";
      search?: string;
      page?: number;
      limit?: number;
    } = {}
  ): Promise<GuestDirectoryResponse> {
    const searchParams = new URLSearchParams();

    if (params.status) searchParams.append("status", params.status);
    if (params.search) searchParams.append("search", params.search);
    if (params.page) searchParams.append("page", params.page.toString());
    if (params.limit) searchParams.append("limit", params.limit.toString());

    const url = `/management/guests?${searchParams.toString()}`;

    const response = await this.callApi({
      method: "GET",
      url,
    });

    return response.data;
  }

  /**
   * Get detailed guest profile
   */
  async getGuestDetails(guestId: string): Promise<GuestDetailedProfile> {
    if (!guestId) {
      throw new GuestProfileError("Guest ID is required");
    }

    const response = await this.callApi({
      method: "GET",
      url: `/management/guests/${guestId}/details`,
    });
    return response.data;
  }

  /**
   * Update guest preferences
   */
  async updateGuestPreferences(
    guestId: string,
    preferences: Array<{ type: string; value: string }>
  ): Promise<PreferenceUpdateResponse> {
    if (!guestId) {
      throw new GuestProfileError("Guest ID is required");
    }

    if (!preferences || !Array.isArray(preferences)) {
      throw new GuestProfileError("Preferences array is required");
    }

    const response = await this.callApi({
      method: "PUT",
      url: `/management/guests/${guestId}/preferences`,
      data: { preferences },
    });
    return response.data;
  }

  /**
   * Get dining request details for a specific request
   */
  async getDiningRequestDetails(requestId: string): Promise<any> {
    if (!requestId) {
      throw new GuestProfileError("Request ID is required");
    }

    const response = await this.callApi({
      method: "GET",
      url: `/management/dining/${requestId}`,
    });
    return response.data;
  }
  async getGuestCharges(
    guestId: string,
    params: { page?: number; limit?: number } = {}
  ): Promise<{
    data: Array<{
      chargeId: string;
      description: string;
      amount: number;
      currency: string;
      isPaid: boolean;
      chargeDate: string;
      serviceDate: string | null;
    }>;
    meta: {
      page: number;
      limit: number;
      totalCount: number;
      totalPages: number;
    };
  }> {
    if (!guestId) {
      throw new GuestProfileError("Guest ID is required");
    }

    const searchParams = new URLSearchParams();
    if (params.page) searchParams.append("page", params.page.toString());
    if (params.limit) searchParams.append("limit", params.limit.toString());

    const response = await this.callApi({
      method: "GET",
      url: `/management/guests/${guestId}/charges?${searchParams.toString()}`,
    });
    return response.data;
  }
}

// Helper function to create API instance
export const createGuestProfilesApi = (
  callApi: ReturnType<typeof useAuthenticatedApi>["callApi"]
) => {
  return new GuestProfilesApi(callApi);
};

export { GuestProfileError };
